import * as THREE from 'three';

const DEFAULT_MAX_VERTEX_COUNT = 500000;

const hasUsableUvAttribute = (geometry) => {
    const position = geometry?.getAttribute?.('position');
    const uv = geometry?.getAttribute?.('uv');

    return !!(
        position &&
        uv &&
        Number(uv.count) === Number(position.count) &&
        Number(uv.itemSize) >= 2
    );
};

const getSafeVertexLimit = (value) => {
    const number = Number(value);

    return Number.isFinite(number) && number > 0
        ? Math.floor(number)
        : DEFAULT_MAX_VERTEX_COUNT;
};

/**
 * Создаёт box-projected UV для произвольной BufferGeometry.
 *
 * По умолчанию готовая UV-развёртка сохраняется. Режим force используется
 * при назначении процедурной гранитной текстуры: встроенная UV модели может
 * быть рассчитана под исходный атлас и давать почти однотонную поверхность.
 *
 * Операция идемпотентна: уже созданная box-развёртка повторно не считается.
 * Группы, индексы, позиции и нормали геометрии не изменяются.
 */
export const ensureBoxProjectedUVs = (
    geometry,
    {
        maxVertexCount = DEFAULT_MAX_VERTEX_COUNT,
        force = false,
    } = {}
) => {
    if (!geometry?.isBufferGeometry) return geometry;

    geometry.userData = geometry.userData || {};

    if (geometry.userData.__boxUvGenerated === true) {
        return geometry;
    }

    const hasExistingUv = hasUsableUvAttribute(geometry);

    if (!force && hasExistingUv) {
        geometry.userData.__hasBoxUVs = true;
        geometry.userData.__boxUvSource = 'existing';
        return geometry;
    }

    const position = geometry.getAttribute('position');

    if (!position || !Number(position.count)) {
        return geometry;
    }

    const safeLimit = getSafeVertexLimit(maxVertexCount);

    if (position.count > safeLimit) {
        geometry.userData.__boxUvSkippedByVertexLimit = true;
        geometry.userData.__boxUvVertexCount = Number(position.count);
        geometry.userData.__boxUvVertexLimit = safeLimit;
        return geometry;
    }

    let normal = geometry.getAttribute('normal');

    if (!normal || Number(normal.count) !== Number(position.count)) {
        geometry.computeVertexNormals();
        normal = geometry.getAttribute('normal');
    }

    if (!normal || Number(normal.count) !== Number(position.count)) {
        return geometry;
    }

    const uvs = new Float32Array(position.count * 2);

    for (let index = 0; index < position.count; index += 1) {
        const nx = Math.abs(normal.getX(index));
        const ny = Math.abs(normal.getY(index));
        const nz = Math.abs(normal.getZ(index));

        const x = position.getX(index);
        const y = position.getY(index);
        const z = position.getZ(index);

        if (nx >= ny && nx >= nz) {
            uvs[index * 2] = z;
            uvs[index * 2 + 1] = y;
        } else if (ny >= nx && ny >= nz) {
            uvs[index * 2] = x;
            uvs[index * 2 + 1] = z;
        } else {
            uvs[index * 2] = x;
            uvs[index * 2 + 1] = y;
        }
    }

    geometry.setAttribute(
        'uv',
        new THREE.BufferAttribute(uvs, 2)
    );

    geometry.getAttribute('uv').needsUpdate = true;
    geometry.userData.__hasBoxUVs = true;
    geometry.userData.__boxUvGenerated = true;
    geometry.userData.__boxUvSource = hasExistingUv
        ? 'forced-replacement'
        : 'generated-missing';
    geometry.userData.__boxUvSkippedByVertexLimit = false;

    return geometry;
};

export default ensureBoxProjectedUVs;
